import { useMemo } from 'react'
import usePersistedFilters from '../../../../hooks/usePersistedFilters'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import { FaArrowCircleLeft, FaArrowCircleRight } from 'react-icons/fa'
import { useSelector } from 'react-redux'

import PageHeader from '../../../../shared/PageHeader'
import ReportFilters from '../../../../shared/ReportFilters'
import ReportJobsList from '../../../../shared/ReportJobsList'
import useAsyncReport from '../../../../hooks/useAsyncReport'
import {
  addLabRadiologySummaryJob,
  updateLabRadiologySummaryJob,
  removeLabRadiologySummaryJob
} from '../../../../redux/slices/AppSlice'
import { validateOneMonthRange, getMinDate, getOneMonthMaxDate } from '../../../../utils/dateRangeValidation'

const LabRadiologySummaryReportAsync = ({ endpointBase = '/v3/reports/items/lab-radiology-summary/jobs', reportTitle = 'Lab & Radiology Summary Report', fileNamePrefix = 'lab_radiology_summary_report', jobs: providedJobs, addJobAction = addLabRadiologySummaryJob, updateJobAction = updateLabRadiologySummaryJob, removeJobAction = removeLabRadiologySummaryJob }) => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const summaryJobs = useSelector(state => state.app.labRadiologySummaryJobs || [])
  const jobs = providedJobs || summaryJobs

  const { filters, setFilters, resetFilters } = usePersistedFilters(
    'lab_radiology_summary_report',
    {
      start_date: moment().subtract(1, 'day').toDate(),
      end_date: moment().toDate(),
      service_type: 'lab',
      format: 'excel'
    },
    { omitKeys: ['item'] }
  )

  const { loading, downloadingJobs, startJob, downloadReport, removeJob, retryJob } = useAsyncReport({
    addJobAction,
    updateJobAction,
    removeJobAction,
    createEndpoint: endpointBase,
    statusEndpoint: endpointBase,
    downloadEndpoint: endpointBase,
    fileNamePrefix,
    existingJobs: jobs,
    buildPayload: (filters, format) => ({
      start_date: moment(filters.start_date).format('YYYY-MM-DD'),
      end_date: moment(filters.end_date).format('YYYY-MM-DD'),
      service_type: filters.service_type || 'lab',
      ...(filters.item?.non_standard_code
        ? { non_standard_code: filters.item.non_standard_code }
        : {}),
      group_by: 'practitioner',
      format
    })
  })

  const handleGenerate = async () => {
    if (!validateOneMonthRange(filters.start_date, filters.end_date, t)) return
    await startJob(filters, filters.format)
  }

  const handleChange = e => {
    const name = e.target.name
    const value = e.target.value
    setFilters(prev => ({ ...prev, [name]: value }))
    if (name === 'service_type') {
      setFilters(prev => ({ ...prev, service_type: value, item: null }))
    }
  }

  const itemOptionsFormat = items => items
    .filter(item => item?.non_standard_code)
    .map(item => ({
      ...item,
      label: `${item?.non_standard_description} | ${item.non_standard_code}`,
      value: item.non_standard_code
    }))

  const filterConfig = useMemo(() => [
    {
      type: 'date',
      name: 'start_date',
      label: 'Start Date',
      width: 'w-[160px]'
    },
    {
      type: 'date',
      name: 'end_date',
      label: 'End Date',
      width: 'w-[160px]',
      minDate: getMinDate(filters.start_date),
      maxDate: getOneMonthMaxDate(filters.start_date)
    },
    {
      type: 'dropdown',
      name: 'service_type',
      label: 'Service Type',
      options: [
        { label: 'Lab', value: 'lab' },
        { label: 'Radiology', value: 'radiology' }
      ],
      width: 'w-[160px]'
    },
    {
      type: 'autocomplete',
      name: 'item',
      label: 'Item',
      placeholder: 'Search item, code or description',
      fieldUrl: '/v3/pricelists/report-items/search',
      queryKey: 'search',
      payload: { service_type: filters.service_type || 'lab' },
      optionsFormat: itemOptionsFormat,
      width: 'w-[300px]'
    },
    {
      type: 'dropdown',
      name: 'format',
      label: 'Format',
      options: [
        { label: 'Excel', value: 'excel' },
        { label: 'PDF', value: 'pdf' }
      ],
      width: 'w-[160px]'
    }
  ], [filters.start_date])

  return (
    <div className='main-page-container !overflow-y-auto scrollbar'>
      <div className='flex items-center justify-between'>
        <PageHeader
          title={t(reportTitle)}
          iconSize={20}
          Icon={i18n.language === 'en' ? FaArrowCircleLeft : FaArrowCircleRight}
          onIconClick={() => navigate(-1)}
        />
      </div>

      <ReportFilters
        filters={filters}
        onChange={handleChange}
        filterConfig={filterConfig}
        onGenerate={handleGenerate}
        onReset={resetFilters}
        loading={loading}
      />

      <ReportJobsList
        jobs={jobs}
        onDownload={downloadReport}
        onRemove={removeJob}
        onRetry={retryJob}
        downloadingJobs={downloadingJobs}
        reportTitle={t(reportTitle)}
      />
    </div>
  )
}

export default LabRadiologySummaryReportAsync
